import { PaginationController } from "./Pagination";
import { filter } from "lodash";

export class PaginationInternalController {
    public paginationCtrl: PaginationController;
    public watcher: any;

    // Pager
    public permalinkKey = "main";
    public permalink: string;
    public limit: number;
    public offset: number;
    public total: number;
    public data: any;

    // Load
    public firstLoad = true;

    public pages: any[];
    public selectedPage: any;

    public static $inject = ["$scope", "$location"];
    constructor(
        private $scope: angular.IScope,
        private $location: angular.ILocationService
    ) {

    }

    public $onInit() {
        this.setPageFromPermalink();
        this.$scope.$on("$locationChangeSuccess", this.setPageFromPermalink.bind(this));
    }

    private setPageFromPermalink() {
        if (this.permalink && parseInt(this.$location.search()[this.permalink], 10)) {
            this.setPage(parseInt(this.$location.search()[this.permalink], 10));
        } else if (this.permalink && !parseInt(this.$location.search()[this.permalink], 10)) {
            this.$location.search(this.permalink, 1).replace();
        }
    }

    /**
     * @param changes
     */
    public $onChanges(changes) {
        // the permalink can either be this.paginationCtrl.permalink or paginationCtrl.permalink[this.permalinkKey]
        // So we set it depending of what is present
        if (this.paginationCtrl.permalink && this.paginationCtrl.permalink[this.permalinkKey]) {
            this.permalink = this.paginationCtrl.permalink[this.permalinkKey];
        } else if (this.paginationCtrl.permalink && typeof this.paginationCtrl.permalink === "string") {
            this.permalink = this.paginationCtrl.permalink;
        } else {
            this.permalink = null;
        }

        if (changes.limit || changes.total) {
            this.pages = this.getPages();
        }

        if (changes.offset || changes.limit || changes.total) {
            this.selectedPage = filter(this.pages, (page: any) => {
                return page.page === this.getCurrentPage();
            }).pop();
        }

        // If only 1 other parameter is set, then do not fetch data. If too many parameters are set use default behaviour
        const otherPermalinks = filter(this.paginationCtrl.permalink, (permalink: string, key: string) => {
            return key !== this.permalinkKey && this.$location.search()[permalink];
        }).length === 1;

        // Since you could have a pagination without permalink we check here if it is set or not as it changes when we get the data
        const permalinkPage = this.permalink ? parseInt(this.$location.search()[this.permalink], 10) : null;

        // When offset & data is set, it means it gets overriden by another component and thus it should not fetch new data
        // When permalinkPage does not equal the current page it means that another page is set via permalink
        // and we shouldn't fetch the current page
        if (
            (changes.offset && !changes.data) ||
            changes.limit &&
            (!permalinkPage || permalinkPage === this.getCurrentPage()) &&
            !otherPermalinks // do not update when another permalink is set
        ) {
            this.fetchPage();
        } else {
            this.firstLoad = false;
        }

        // Only fetch data when watcher changes
        if (
            changes.watcher && (!changes.watcher.previousValue ||
                (changes.watcher.previousValue.constructor && changes.watcher.previousValue.constructor.name !== "UNINITIALIZED_VALUE"))
        ) {
            this.fetchPage();
        }
    }

    public fetchPage() {
        this.paginationCtrl.setLoading(true);
        this.paginationCtrl.getData({ limit: this.limit, offset: this.offset })
            .then((object) => {
                this.paginationCtrl.setTotal(object.total);
                this.paginationCtrl.setData(object.data);
            })
            .finally(() => {
                this.paginationCtrl.setLoading(false);
                this.firstLoad = false;
            });
    }

    /**
     * @returns {Array}
     */
    public getPages(): any[] {
        if (!this.total) {
            return;
        }

        let pages = [];
        for (let i = 0; i < this.getTotalPages(); i++) {
            let min = this.limit * i + 1;
            let max = this.limit * i + this.limit;

            if (this.total < max) {
                max = this.total;
            }

            pages.push({
                page: i + 1,
                value: min + ". - " + max + "."
            });
        }

        return pages;
    }

    /**
     * Get the amount of total pages
     * @returns {number}
     */
    public getTotalPages(): number {
        if (!this.total) {
            return 1;
        }

        return Math.ceil(this.total / this.limit);
    }

    /**
     * Gets the current page number
     * @returns {number}
     */
    public getCurrentPage(): number {
        if (this.offset < this.limit) {
            return 1;
        }

        let offset = this.offset;

        if (this.total) {
            offset = Math.min(this.total - this.limit, offset);
        }

        return Math.ceil(offset / this.limit) + 1;
    }

    /**
     * Gets the next page number
     * @returns {number}
     */
    public getNextPage(): number {
        if (this.getTotalPages() === this.getCurrentPage()) {
            return null;
        }

        return this.getCurrentPage() + 1;
    }

    /**
     * Gets the previous page number
     * @returns {number}
     */
    public getPreviousPage(): number {
        if (this.getCurrentPage() === 1) {
            return null;
        }

        return this.getCurrentPage() - 1;
    }

    /**
     * @param number Page number in human readable format (start index of 1)
     */
    public setPage(number: number): void {
        this.paginationCtrl.setOffset(this.limit * (number - 1));

        if (this.permalink && number !== parseInt(this.$location.search()[this.permalink], 100)) {
            this.$location.search(this.permalink, number);
        }

        if (this.paginationCtrl.permalink) {
            this.paginationCtrl.cleanUpPermalinks(this.permalinkKey);
        }
    }

    /**
     * Sets the current page to the next page
     */
    public setNextPage(): void {
        this.setPage(this.getNextPage());
    }

    /**
     * Sets the current page to the previous page
     */
    public setPreviousPage(): void {
        this.setPage(this.getPreviousPage());
    }
}